import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Layout,
  setAutomaticRefresh,
  setSidebarVisibility,
  Sidebar,
  useGetIdentity,
  useIsAutomaticRefreshEnabled,
} from "react-admin";
import AppBar from "./AppBar";
import Menu from "./Menu";
import { AppState } from "../../common/types";
import CssBaseline from "@material-ui/core/CssBaseline";
import { isEmpty } from "lodash";
import { LayoutContext, layoutContextReducer } from "./LayoutContext";
import "./Layout.css";
import { useActionPermissions } from "../../common/auth/useActionPermissions";
import { makeStyles } from "@material-ui/styles";
import classNames from "classnames";
import { useEffect, useMemo } from "react";
import { Theme, Typography, useMediaQuery } from "@material-ui/core";
import { useProLogsTheme } from "../../common/hooks/useProLogsTheme";
import { useThemeCustomPallete } from "../../common/hooks/useThemeCustomPallete";
import bgImageDark from "../../common/images/architecture-1869211_1920_bg_black.png";
import bgImageLight from "../../common/images/architecture-1869211_1920_bg_white.png";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: "calc(100vh - 4em)",
  },
}));

const useMainContentStyles = makeStyles<
  Theme,
  { isSidebarOpen: boolean; appbarHeight: number }
>((_) => ({
  mainContent: (props) => ({
    maxWidth: props.isSidebarOpen
      ? window.innerWidth > 599
        ? "calc(100vw - 240px)"
        : "100vw"
      : window.innerWidth > 599
      ? "calc(100vw - 70px)"
      : "100vw",
    height: `calc(100vh - ${props.appbarHeight}px - 24px)`,
  }),
}));

const CustomSidebar = ({ className, ...props }: any) => {
  const classes = useStyles();
  return <Sidebar className={classNames(className, classes.root)} {...props} />;
};

const LayoutComponent = ({ children, ...props }: any) => {
  const reduxDispatch = useDispatch();
  const { identity } = useGetIdentity();
  const isAutoRefresh = useIsAutomaticRefreshEnabled();
  const permissions = useActionPermissions();
  const isXSmall = useMediaQuery("(min-width:1080px)");

  const Sidebar =
    !!permissions && !isEmpty(permissions) ? CustomSidebar : () => null;
  const theme = useProLogsTheme();
  const themeType = useSelector((state: AppState) => state.theme);

  const customThemePalette = useThemeCustomPallete();

  const [layoutContext, dispatch] = React.useReducer(layoutContextReducer, {
    appBarHeight: 0,
  });

  const isSidebarOpen =
    useSelector((x: AppState) => x.admin.ui.sidebarOpen) && !!identity?.id;
  const classes = useMainContentStyles({
    isSidebarOpen,
    appbarHeight: layoutContext.appBarHeight,
  });

  useEffect(() => {
    if (!identity?.id) {
      return;
    }

    if (!permissions || isEmpty(permissions)) {
      reduxDispatch(setSidebarVisibility(false));
    } else {
      reduxDispatch(setSidebarVisibility(true));
    }
  }, [permissions, reduxDispatch, identity]);

  useEffect(() => {
    if (!identity?.id) {
      return;
    }

    if (isAutoRefresh) {
      reduxDispatch(setAutomaticRefresh(false));
    }
  }, [isAutoRefresh, reduxDispatch, identity]);

  const layoutContextValue = useMemo(() => {
    return { ...layoutContext, dispatch };
  }, [layoutContext]);

  return (
    <>
      <CssBaseline />
      <style>
        {`html {
          -webkit-font-smoothing: auto;
          -moz-osx-font-smoothing: auto;
        }

        #main-content .MuiPaper-root {
          background-color: ${
            customThemePalette?.override.MuiPapper ?? "rgba(255, 255, 255, 0.1)"
          };
        }        
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"
        }`}
      </style>
      <LayoutContext.Provider value={layoutContextValue}>
        <Layout
          {...props}
          appBar={AppBar}
          sidebar={Sidebar}
          menu={Menu}
          theme={theme}
          style={{
            backgroundImage: `url(${
              process.env.REACT_APP_API_URL?.replace(/\/+$/, "") +
              `/branding/bg_${themeType}.png`
            }), url('${themeType === "dark" ? bgImageDark : bgImageLight}')`,
          }}
          className={`prologs-layout-${theme.palette.type}`}
        >
          {!isXSmall && (
            <div style={{ background: theme.palette.secondary.main }}>
              <Typography
                variant="h6"
                color="inherit"
                id="react-admin-title"
                className="react-admin-title-mobile"
              />
            </div>
          )}
          <div className={classes.mainContent}>{children}</div>
        </Layout>
      </LayoutContext.Provider>
    </>
  );
};

export default (props: any) => <LayoutComponent {...props} />;
